import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, ArrowRight } from 'lucide-react';
import { CopilotInputBar } from '@/components/ai-agents/input-bar/CopilotInputBar';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';

interface QuickChatWelcomeProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
}

export const QuickChatWelcome: React.FC<QuickChatWelcomeProps> = ({
  onSendMessage,
  isLoading = false
}) => {
  // Get input value and setter from localStorage state for sync with AI chat modal
  const { inputValue, setInputValue } = useLocalStorageState();
  
  const starterPrompts = [
    "Help me brainstorm business ideas",
    "Create a marketing strategy",
    "Write compelling sales copy",
    "Research my competition"
  ];

  const handlePromptClick = (prompt: string) => {
    // Send message - useQuickChat will handle setting input value at the right time
    onSendMessage(prompt);
  };

  return (
    <div className="space-y-4">
      {/* Welcome Message */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3"
      >
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
          <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            Hi! I'm your AI assistant. Ready to start your first conversation?
          </p>
        </div>
      </motion.div>

      {/* Starter Prompts */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-2"
      >
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
          Try these to get started:
        </p>
        <div className="grid grid-cols-1 gap-2">
          {starterPrompts.map((prompt, index) => (
            <motion.button
              key={index}
              onClick={() => handlePromptClick(prompt)}
              disabled={isLoading}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              whileHover={{ 
                scale: 1.02,
                y: -2,
                transition: { 
                  duration: 0.2, 
                  ease: "easeOut"
                }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { 
                  duration: 0.1, 
                  ease: "easeInOut"
                }
              }}
              className="group relative flex items-center justify-between p-3 text-left text-xs rounded-lg border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm hover:shadow-lg hover:border-blue-300/60 dark:hover:border-blue-600/60 hover:bg-blue-50/80 dark:hover:bg-blue-900/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
              style={{
                willChange: 'transform'
              }}
            >
              {/* Shimmer effect on hover */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/10 to-transparent skew-x-12"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
              />
              
              <span className="relative z-10 text-gray-700 dark:text-gray-300 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-200">
                {prompt}
              </span>
              
              <motion.div
                className="relative z-10"
                whileHover={{ 
                  x: 2,
                  transition: { duration: 0.2 }
                }}
              >
                <ArrowRight className="h-3 w-3 text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
              </motion.div>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Input Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <CopilotInputBar
          onSendMessage={onSendMessage}
          isLoading={isLoading}
          placeholder="Ask me anything about your business..."
          minRows={1}
          maxRows={3}
          value={inputValue}
          onChange={setInputValue}
        />
      </motion.div>
    </div>
  );
}; 